import { resolvePolicyStatus, resolveQuoteStatus } from './coverageStatus';

/**
 * Picks the one policy and the one quote the quick-action rail should speak for.
 *
 * `/policy/v1/latest` and `/quote/v1/latest` used to return a single row each and now
 * return every active policy and every ready quote (fir-account#6228). The rail's cards
 * take a raw policy or quote -- `quoteId`, `product`, `customerEmail` and the rest -- so
 * this selects from the arrays rather than normalising, which would drop the very fields
 * the cards read.
 *
 * Selection is deliberate, not positional: an account with two policies would otherwise
 * get whichever one Elasticsearch happened to return first.
 */

type Raw = Record<string, any>;

/** Coverage in force outranks coverage that has not started, which outranks a binder. */
const POLICY_RANK: Record<string, number> = {
  inForce: 0,
  issued: 1,
  bound: 2,
};

const effectiveTime = (raw: Raw): number => {
  const parsed = Date.parse(raw?.effectiveDate ?? '');

  return Number.isNaN(parsed) ? 0 : parsed;
};

const expiryTime = (raw: Raw): number => {
  const parsed = Date.parse(raw?.expiresOn ?? '');

  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
};

export interface QuickActionSubjects {
  readonly policy: Raw;
  readonly quote: Raw;
}

/**
 * Empty objects rather than undefined for a missing side: the rail's branches read
 * properties straight off these and are written against `{}`.
 */
export function selectQuickActionSubjects(
  policies: readonly Raw[] = [],
  quotes: readonly Raw[] = [],
  now: string | number | Date = Date.now()
): QuickActionSubjects {
  const ranked = policies
    .filter(Boolean)
    .map((raw) => ({
      raw,
      rank: POLICY_RANK[
        resolvePolicyStatus({
          rawStatus: raw.status,
          effectiveDate: raw.effectiveDate,
          termEnd: raw.expiresOn ?? raw.endDate,
          isPaid: raw.isPaid,
          isAppSigned: raw.isAppSigned,
          now,
        })
      ],
    }))
    // A status outside those three is not something the rail can offer an action on.
    .filter((entry) => entry.rank !== undefined)
    // Newest term first within a rank, so a renewal already bound wins over the term it
    // replaces.
    .sort(
      (a, b) => a.rank - b.rank || effectiveTime(b.raw) - effectiveTime(a.raw)
    );

  const actionable = quotes
    .filter(Boolean)
    .filter((raw) => {
      const status = resolveQuoteStatus({
        rawStatus: raw.customerStatus ?? raw.status,
        expiresOn: raw.expiresOn,
        canBind: raw.canBind,
        now,
      });

      return status === 'quoteReady' || status === 'quoteBlocked';
    })
    // Soonest to expire: the decision with the least time left on it.
    .sort((a, b) => expiryTime(a) - expiryTime(b));

  return {
    policy: ranked[0]?.raw ?? {},
    quote: actionable[0] ?? {},
  };
}
